import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { refreshAccessToken, setIdleTimerReset } from "./axiosConfig";
import { logoutAndRedirect } from "./apiCalls/auth";
import { getAccessToken } from "../services/auth";

const IDLE_TIMEOUT = 15 * 60 * 1000;
const WARNING_TIME = 2 * 60 * 1000;
const LAST_ACTIVITY_KEY = "healthetile_last_activity";

const useIdleTimer = () => {
  const location = useLocation();
  const timerRef = useRef(null);
  const warningTimerRef = useRef(null);
  const countdownTimerRef = useRef(null);
  const showWarningRef = useRef(false);
  const loggingOutRef = useRef(false);
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const clearAllTimers = useCallback(() => {
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    if (warningTimerRef.current) {
      window.clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }

    if (countdownTimerRef.current) {
      window.clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
  }, []);

  const dismissWarning = useCallback(() => {
    setShowWarning(false);
    showWarningRef.current = false;
    setCountdown(0);

    if (countdownTimerRef.current) {
      window.clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
  }, []);

  const logout = useCallback(() => {
    loggingOutRef.current = true;
    clearAllTimers();
    setIsLoggingOut(true);
    logoutAndRedirect();
  }, [clearAllTimers]);

  const startCountdown = useCallback((initialSeconds) => {
    setCountdown(initialSeconds ?? Math.ceil(WARNING_TIME / 1000));

    countdownTimerRef.current = window.setInterval(() => {
      setCountdown((previousCountdown) => Math.max(previousCountdown - 1, 0));
    }, 1000);
  }, []);

  // elapsedMs lets a wake-from-sleep check schedule against real time already
  // passed, instead of a fresh 15+2 min window that ignores how long the
  // laptop was actually asleep.
  const scheduleTimers = useCallback(
    (elapsedMs = 0) => {
      // The logout request and any mouse movement both land here; neither may
      // restart the timers or dismiss the "Logging Out" state mid-request.
      if (loggingOutRef.current) return;

      clearAllTimers();

      if (!getAccessToken()) {
        dismissWarning();
        return;
      }

      setIsLoggingOut(false);
      setShowWarning(false);
      showWarningRef.current = false;
      setCountdown(0);

      const remainingToWarning = IDLE_TIMEOUT - elapsedMs;
      const remainingToLogout = IDLE_TIMEOUT + WARNING_TIME - elapsedMs;

      if (remainingToLogout <= 0) {
        logout();
        return;
      }

      if (remainingToWarning <= 0) {
        setShowWarning(true);
        showWarningRef.current = true;
        startCountdown(Math.ceil(remainingToLogout / 1000));
        return;
      }

      warningTimerRef.current = window.setTimeout(() => {
        setShowWarning(true);
        showWarningRef.current = true;
        startCountdown();
      }, remainingToWarning);

      timerRef.current = window.setTimeout(() => {
        logout();
      }, remainingToLogout);
    },
    [clearAllTimers, dismissWarning, logout, startCountdown],
  );

  const resetTimer = useCallback(() => {
    localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
    scheduleTimers(0);
  }, [scheduleTimers]);

  useEffect(() => {
    if (!showWarning || countdown > 0) return;

    if (countdownTimerRef.current) {
      window.clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
    logout();
  }, [countdown, showWarning, logout]);

  const handleStayLoggedIn = useCallback(async () => {
    try {
      await refreshAccessToken();
      dismissWarning();
      resetTimer();
    } catch {
      logout();
    }
  }, [dismissWarning, logout, resetTimer]);

  useEffect(() => {
    if (!getAccessToken()) {
      setIdleTimerReset(null);
      clearAllTimers();
      dismissWarning();
      return undefined;
    }

    setIdleTimerReset(resetTimer);

    const events = [
      "mousedown",
      "mouseup",
      "mousemove",
      "keydown",
      "keyup",
      "scroll",
      "touchstart",
      "touchend",
      "click",
    ];

    const handleActivity = () => {
      if (!showWarningRef.current) {
        resetTimer();
      }
    };

    // setTimeout doesn't run while the OS suspends the tab (laptop sleep),
    // so a pending 15/17 min timer just resumes with time left on it when the
    // machine wakes, blind to however long it was actually asleep. Recompute
    // against the wall-clock timestamp instead whenever the tab becomes
    // visible again.
    const handleVisibility = () => {
      if (document.visibilityState !== "visible") return;
      if (!getAccessToken()) return;

      const lastActivity = Number(localStorage.getItem(LAST_ACTIVITY_KEY)) || Date.now();
      scheduleTimers(Date.now() - lastActivity);
    };

    events.forEach((event) => {
      window.addEventListener(event, handleActivity, true);
    });
    document.addEventListener("visibilitychange", handleVisibility);

    resetTimer();

    return () => {
      setIdleTimerReset(null);
      clearAllTimers();
      events.forEach((event) => {
        window.removeEventListener(event, handleActivity, true);
      });
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [clearAllTimers, dismissWarning, location.pathname, resetTimer, scheduleTimers]);

  return {
    showWarning,
    countdown,
    handleStayLoggedIn,
    handleLogout: logout,
    isLoggingOut,
  };
};

export default useIdleTimer;
